/**
 * Système de logging sécurisé pour l'application Veolia
 * - Logs conditionnels selon l'environnement
 * - Sanitisation des données sensibles
 * - Niveaux de log appropriés
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: unknown;
  timestamp: string;
  context?: string;
}

class SecureLogger {
  private isProduction: boolean;
  private isDevelopment: boolean;
  private allowedLevels: LogLevel[];

  constructor() {
    this.isProduction = process.env.NODE_ENV === 'production';
    this.isDevelopment = process.env.NODE_ENV === 'development';
    
    // En production, on ne log que les warnings et erreurs
    this.allowedLevels = this.isProduction 
      ? ['warn', 'error'] 
      : ['debug', 'info', 'warn', 'error'];
  }

  /**
   * Sanitise les données pour éviter l'exposition d'informations sensibles
   */
  private sanitizeData(data: unknown): unknown {
    if (!data) return data;
    
    if (typeof data === 'string') {
      // Masquer les tokens, clés API, emails, etc.
      return data
        .replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[EMAIL_MASKED]')
        .replace(/Bearer\s+[A-Za-z0-9\-._~+/]+=*/g, 'Bearer [TOKEN_MASKED]')
        .replace(/[A-Za-z0-9]{20,}/g, (match) => {
          // Masquer les clés longues (probablement des tokens)
          return match.length > 20 ? '[KEY_MASKED]' : match;
        });
    }
    
    if (Array.isArray(data)) {
      return data.map(item => this.sanitizeData(item));
    }
    
    if (typeof data === 'object') {
      const sanitized: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(data)) {
        // Masquer les champs sensibles
        if (['password', 'token', 'key', 'secret', 'email', 'phone'].some(sensitive => 
          key.toLowerCase().includes(sensitive)
        )) {
          sanitized[key] = '[MASKED]';
        } else {
          sanitized[key] = this.sanitizeData(value);
        }
      }
      return sanitized;
    }
    
    return data;
  }

  /**
   * Crée une entrée de log formatée
   */
  private createLogEntry(level: LogLevel, message: string, data?: unknown, context?: string): LogEntry {
    return {
      level,
      message,
      data: data ? this.sanitizeData(data) : undefined,
      timestamp: new Date().toISOString(),
      context
    };
  }

  /**
   * Log de debug (développement uniquement)
   */
  debug(message: string, data?: unknown, context?: string): void {
    if (this.allowedLevels.includes('debug')) {
      const entry = this.createLogEntry('debug', message, data, context);
      console.debug(`[DEBUG] ${entry.timestamp} ${context ? `[${context}] ` : ''}${message}`, entry.data);
    }
  }

  /**
   * Log d'information (développement uniquement)
   */
  info(message: string, data?: unknown, context?: string): void {
    if (this.allowedLevels.includes('info')) {
      const entry = this.createLogEntry('info', message, data, context);
      console.info(`[INFO] ${entry.timestamp} ${context ? `[${context}] ` : ''}${message}`, entry.data);
    }
  }

  /**
   * Log d'avertissement (tous environnements)
   */
  warn(message: string, data?: unknown, context?: string): void {
    if (this.allowedLevels.includes('warn')) {
      const entry = this.createLogEntry('warn', message, data, context);
      console.warn(`[WARN] ${entry.timestamp} ${context ? `[${context}] ` : ''}${message}`, entry.data);
    }
  }

  /**
   * Log d'erreur (tous environnements)
   */
  error(message: string, error?: unknown, context?: string): void {
    if (this.allowedLevels.includes('error')) {
      const entry = this.createLogEntry('error', message, error, context);
      console.error(`[ERROR] ${entry.timestamp} ${context ? `[${context}] ` : ''}${message}`, entry.data);
      
      // En production, on peut envoyer les erreurs à un service de monitoring
      if (this.isProduction && typeof window !== 'undefined') {
        // Ici on pourrait intégrer Sentry ou un autre service
        // Sentry.captureException(error, { extra: entry.data });
      }
    }
  }

  /**
   * Log de sécurité pour les tentatives d'attaque
   */
  security(message: string, data?: unknown): void {
    // Les logs de sécurité sont toujours enregistrés
    const entry = this.createLogEntry('warn', `SECURITY: ${message}`, data, 'SECURITY');
    console.warn(`[SECURITY] ${entry.timestamp} ${message}`, entry.data);
    
    // En production, on pourrait envoyer une alerte
    if (this.isProduction) {
      // Intégration avec un système d'alerte
    }
  }
}

// Instance singleton
export const logger = new SecureLogger();

// Export des méthodes pour faciliter l'utilisation
export const { debug, info, warn, error, security } = logger;
